import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatNative, type PreviewAmount } from "@auto-pools/core";
import { formatMoney, formatToken, formatUsd, shorten } from "../lib/format";
import type { Position } from "../types";

const EDGE = 8;

function tokenAmount(amount: PreviewAmount, position: Position) {
  const token = [position.tokenA, position.tokenB].find((item) => item.mint === amount.mint);
  const formatted = formatToken(amount.decimal, "long");
  return `${formatted === "Unavailable" ? "—" : formatted} ${token?.symbol ?? shorten(amount.mint)}`;
}

function solFee(lamports: string | null) {
  if (lamports === null) return "—";
  try {
    return `${formatNative(BigInt(lamports), 9)} SOL`;
  } catch {
    return "—";
  }
}

function displayMoney(value: string | null) {
  const formatted = formatMoney(value);
  return formatted === "Unavailable" ? "—" : formatted;
}

function AmountRow({
  label,
  amount,
  position,
}: {
  label: string;
  amount: PreviewAmount | null;
  position: Position;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {amount ? tokenAmount(amount, position) : "—"}
        {amount?.usd !== null && amount?.usd !== undefined && (
          <small>{formatUsd(amount.usd) === "Unavailable" ? "—" : formatUsd(amount.usd)}</small>
        )}
      </dd>
    </div>
  );
}

export function PositionPnl({ position }: { position: Position }) {
  const [open, setOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ left: EDGE, top: EDGE });
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | null>(null);
  const restoringFocus = useRef(false);
  const dialogId = useId();
  const titleId = useId();

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  const close = useCallback(
    (restoreFocus = false) => {
      cancelClose();
      setOpen(false);
      if (restoreFocus)
        requestAnimationFrame(() => {
          restoringFocus.current = true;
          trigger.current?.focus();
          restoringFocus.current = false;
        });
    },
    [cancelClose],
  );
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      const focused = document.activeElement;
      if (!popup.current?.contains(focused) && focused !== trigger.current) setOpen(false);
    }, 120);
  }, [cancelClose]);
  const place = useCallback(() => {
    const anchor = trigger.current;
    const panel = popup.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const left = Math.min(Math.max(EDGE, a.left), Math.max(EDGE, innerWidth - p.width - EDGE));
    const below = a.bottom + 6;
    const top =
      below + p.height <= innerHeight - EDGE ? below : Math.max(EDGE, a.top - p.height - 6);
    setPopupPosition({ left, top });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popup.current?.contains(target) && !trigger.current?.contains(target)) close();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    window.addEventListener("pointerdown", outside);
    window.addEventListener("keydown", key);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("pointerdown", outside);
      window.removeEventListener("keydown", key);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [close, open, place]);
  useEffect(
    () => () => {
      cancelClose();
    },
    [cancelClose],
  );

  const breakdown = position.pnlBreakdown;
  const value = displayMoney(position.pnlUsd);
  return (
    <div className="position-metric position-pnl-metric">
      <small>Net PnL</small>
      <button
        ref={trigger}
        className={`pnl-trigger ${
          position.pnlUsd !== null && Number(position.pnlUsd) >= 0 ? "positive" : ""
        } ${position.pnlUsd === null ? "unknown" : ""}`}
        aria-label={`Net PnL ${value}. Show calculation`}
        aria-expanded={open}
        aria-controls={dialogId}
        aria-haspopup="dialog"
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          if (restoringFocus.current) return;
          cancelClose();
          setOpen(true);
        }}
        onBlur={scheduleClose}
        onClick={() => {
          cancelClose();
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" && (event.key !== "Tab" || event.shiftKey)) return;
          event.preventDefault();
          cancelClose();
          setOpen(true);
          requestAnimationFrame(() => closeButton.current?.focus());
        }}
      >
        {value}
        <span aria-hidden="true">ⓘ</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={popup}
            id={dialogId}
            className="pnl-popup"
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            style={popupPosition}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onFocusCapture={cancelClose}
            onBlurCapture={scheduleClose}
          >
            <div className="pnl-popup-heading">
              <strong id={titleId}>How Net PnL is calculated</strong>
              <button
                ref={closeButton}
                onClick={() => {
                  close(true);
                }}
              >
                Close
              </button>
            </div>
            {!breakdown || breakdown.status === "unavailable" ? (
              <p className="pnl-unavailable">
                {breakdown?.reason ?? "Opening cost or current asset details are unavailable."}
              </p>
            ) : (
              <>
                <p>
                  Net PnL is total assets minus admitted opening capital. All incurred costs are
                  already reflected in this result.
                </p>
                <dl className="pnl-formula">
                  <div>
                    <dt>Total assets</dt>
                    <dd>{displayMoney(breakdown.totalAssetsUsd)}</dd>
                  </div>
                  <div>
                    <dt>Admitted opening capital</dt>
                    <dd>{displayMoney(breakdown.baselineCapitalUsd)}</dd>
                  </div>
                  <div className="pnl-formula-result">
                    <dt>Net PnL</dt>
                    <dd>{value}</dd>
                  </div>
                </dl>
                <h4>Opening capital</h4>
                <dl className="pnl-detail-list">
                  <AmountRow
                    label="Funding"
                    amount={breakdown.baselineFunding}
                    position={position}
                  />
                  <AmountRow
                    label="Expense reserve"
                    amount={breakdown.baselineExpenseReserve}
                    position={position}
                  />
                </dl>
                <h4>Current assets</h4>
                <dl className="pnl-detail-list">
                  <div>
                    <dt>LP value</dt>
                    <dd>{displayMoney(breakdown.lpUsd)}</dd>
                  </div>
                  <div>
                    <dt>Fees</dt>
                    <dd>{displayMoney(breakdown.feesUsd)}</dd>
                  </div>
                  <div>
                    <dt>Rewards</dt>
                    <dd>{displayMoney(breakdown.rewardsUsd)}</dd>
                  </div>
                  {breakdown.residualAndUnusedExpense?.map((amount) => (
                    <AmountRow
                      key={`${amount.mint}:${amount.native}`}
                      label="Residual or unused reserve"
                      amount={amount}
                      position={position}
                    />
                  )) ?? (
                    <AmountRow
                      label="Residual or unused reserve"
                      amount={null}
                      position={position}
                    />
                  )}
                  <AmountRow
                    label="Recoverable account deposit"
                    amount={breakdown.recoverableRent}
                    position={position}
                  />
                </dl>
                <h4>Incurred costs memo</h4>
                <dl className="pnl-detail-list">
                  <div>
                    <dt>Network fees</dt>
                    <dd>
                      {solFee(breakdown.incurredCosts.networkFeeLamports)}
                      <small>
                        {displayMoney(breakdown.incurredCosts.networkFeeUsdAtOpeningMarks)}
                      </small>
                    </dd>
                  </div>
                  <div>
                    <dt>Opening swap value difference</dt>
                    <dd>
                      {displayMoney(breakdown.incurredCosts.swapValueDifferenceUsdAtOpeningMarks)}
                    </dd>
                  </div>
                </dl>
                <p className="pnl-memo-note">
                  Opening quote USD marks value these costs. They are shown for attribution and are
                  already included, so they are not subtracted again. Unused reserved budget remains
                  part of current assets.
                </p>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
